// Early Access signup page — captures pre-launch interest in Free or Pro tier.
// Tier is read from the ?tier= query parameter (free | pro). Defaults to free.

import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Mail, ArrowRight, Check, ArrowLeft, Sparkles } from 'lucide-react';
import { Layout } from '../components/Layout';
import { api } from '../services/api';

type Tier = 'free' | 'pro';

const TIER_COPY: Record<Tier, { eyebrow: string; title: string; description: string }> = {
  free: {
    eyebrow: 'Free — Early Access',
    title: 'Get notified when Free is live',
    description:
      'Free tier lets you build a complete proof of concept on your own infrastructure. We will email you the moment it ships, with the install command and a quickstart guide.',
  },
  pro: {
    eyebrow: 'Pro — Early Access',
    title: 'Get notified when Pro is live',
    description:
      'Pro adds cloud LLM providers, business intelligence, file handling and white-labeling on top of Free. Drop your email and we will let you know the moment subscriptions open.',
  },
};

function isValidTier(value: string | null): value is Tier {
  return value === 'free' || value === 'pro';
}

export function EarlyAccess() {
  const [searchParams] = useSearchParams();
  const rawTier = searchParams.get('tier');
  const tier: Tier = isValidTier(rawTier) ? rawTier : 'free';
  const source = searchParams.get('source') || undefined;

  const copy = useMemo(() => TIER_COPY[tier], [tier]);

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = `${copy.title} — Diosc Hub`;
  }, [copy.title]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await api.requestEarlyAccess(email, tier, source);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to pricing
        </Link>

        <div className="mb-10">
          <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accent mb-3">
            {tier === 'pro' && <Sparkles className="h-3 w-3" />}
            {copy.eyebrow}
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
            {copy.title}
          </h1>
          <p className="text-base text-muted-foreground leading-relaxed">{copy.description}</p>
        </div>

        <div className="bg-card border border-border rounded-xl p-8">
          {submitted ? (
            <div className="flex items-start gap-4">
              <div className="rounded-full bg-emerald-500/10 p-2">
                <Check className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-1">You&apos;re on the list</h2>
                <p className="text-sm text-muted-foreground">
                  We&apos;ll email <span className="text-foreground">{email}</span> the moment {tier === 'pro' ? 'Pro' : 'Free'} is available. No
                  spam in the meantime.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-accent uppercase tracking-wider mb-2"
              >
                Email Address
              </label>
              <div className="relative mb-5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-2.5 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-ring transition-colors"
                  placeholder="your.email@example.com"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full inline-flex items-center justify-center gap-2 bg-accent text-white font-medium py-2.5 px-4 rounded-md hover:bg-accent/90 transition-all disabled:opacity-50 disabled:pointer-events-none text-sm"
              >
                {isLoading ? 'Adding you...' : 'Notify me at launch'}
                {!isLoading && <ArrowRight className="h-4 w-4" />}
              </button>

              {error && (
                <div className="mt-5 p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <p className="text-xs text-muted-foreground mt-5 leading-relaxed">
                We&apos;ll only use your email to notify you about {tier === 'pro' ? 'Pro' : 'Free'} availability and a single
                launch announcement. Unsubscribe with one click anytime.
              </p>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
}
